import {useEffect, useMemo, useState} from 'react'
import {Card, Spinner, Button, Row, Col, Form, Modal} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {FormattedMessage, useIntl} from 'react-intl'
import {TWILIO_NUMBERS_INVENTORY} from '../../gql/queries/twilioBilling'
import {RELEASE_TWILIO_NUMBER, ADD_NUMBERS_TO_POOL} from '../../gql/mutations/twilioBilling'
import {useTableSort} from '../../helpers/useTableSort'
import {US_STATES} from '../../helpers/catalogs'
import {TELEPHONY_MESSAGES as T} from '../../helpers/telephonyMessages'
import {
  BILLING_MESSAGES as M,
  EM_DASH,
  classLabel,
  type Classification,
} from './twilioBillingMessages'
import {useGetUser} from '../../store/userStore'

const money = (n: number | null | undefined) =>
  n == null ? EM_DASH : `$${Number(n).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

// 2-letter code → full state name (from the shared US_STATES catalog).
const STATE_NAME: Record<string, string> = Object.fromEntries(
  US_STATES.map((s: any) => [s.code || s.value, s.name || s.label])
)
const stateName = (code: string) => (code ? STATE_NAME[code] || code : EM_DASH)
const dateOnly = (s: string) => (s ? s.slice(0, 10) : EM_DASH)

// "Florida (FL)" — a proper noun plus its postal code, so no message needed;
// hoisted out of JSX to keep the parentheses from reading as loose literals.
const stateOption = (s: any) => `${s.name || s.label} (${s.code || s.value})`

type InvNumber = {
  phone_number: string
  friendly_name: string
  type: string
  state: string
  rental_estimate: number
  date_created: string
  owner: string
  group: string
  organization: string
  classification: Classification
  last_used: string
  stale: boolean
}
type Inventory = {
  count: number
  total_rental_estimate: number
  counts: Record<string, number>
  unclassified_count: number
  stale_pool_count: number
  numbers: InvNumber[]
}

const CLASS_BADGE: Record<Classification, string> = {
  pool: 'badge-light-primary',
  policydial: 'badge-light-success',
  messaging: 'badge-light-info',
  unclassified: 'badge-light-warning',
}

const PAGE_SIZE = 15

// Billing → Inventory numbers: the owned-number inventory (with cleanup) plus an
// "Add number to pool" button that opens a buy modal; on success the modal lists
// the purchased numbers with their state and cost, then refetches the inventory.
export const NumbersInventoryTab = () => {
  const intl = useIntl()
  const {data: iData, loading: iLoading, error: iError, refetch: refetchInv} = useQuery(TWILIO_NUMBERS_INVENTORY, {
    fetchPolicy: 'cache-and-network',
  })
  const [releaseNumber] = useMutation(RELEASE_TWILIO_NUMBER)
  const [addToPool, {loading: buyingPool}] = useMutation(ADD_NUMBERS_TO_POOL)

  const inventory: Inventory | undefined = iData?.twilioNumbersInventory

  // The Organization column is a platform-superadmin view (org-admins are already
  // scoped to their own org by the backend, so it would just repeat their org).
  const userGroups = useGetUser((s: any) => s.user?.groups)
  const isSuperAdmin = Array.isArray(userGroups) && userGroups.includes('SuperAdministrador')

  const [reviewOnly, setReviewOnly] = useState(false)
  const [classFilter, setClassFilter] = useState<Classification | ''>('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState('')

  // "Add number to pool" modal + the purchase result shown once it finishes.
  const [showBuy, setShowBuy] = useState(false)
  const [bought, setBought] = useState<any>(null)
  const [poolState, setPoolState] = useState('')
  const [poolArea, setPoolArea] = useState('')
  const [poolQty, setPoolQty] = useState('1')

  const {onSort, sortRows, arrow} = useTableSort<any>('', 'desc')

  const filtered = useMemo(() => {
    let list = inventory?.numbers ?? []
    if (classFilter) list = list.filter((n) => n.classification === classFilter)
    if (reviewOnly) list = list.filter((n) => n.classification === 'unclassified' || n.stale)
    const q = search.replace(/\D/g, '')
    if (q) list = list.filter((n) => n.phone_number.replace(/\D/g, '').includes(q))
    return sortRows(list)
  }, [inventory, reviewOnly, search, classFilter, sortRows])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => {
    setPage(1)
  }, [search, reviewOnly, classFilter])

  const openBuy = () => {
    setBought(null)
    setPoolArea('')
    setPoolQty('1')
    setShowBuy(true)
  }
  const closeBuy = () => {
    setShowBuy(false)
    setBought(null)
  }

  const handleAddToPool = async () => {
    const qty = Math.max(1, Math.min(10, parseInt(poolQty || '1', 10) || 1))
    if (!poolState) {
      toast.warning(intl.formatMessage(M.invToastPickState))
      return
    }
    try {
      const {data} = await addToPool({
        variables: {state: poolState, areaCode: poolArea || null, quantity: qty},
      })
      const res = data?.addNumbersToPool
      if (res?.success) {
        toast.success(res.message || intl.formatMessage(M.invToastAddedToPool))
        ;(res.warnings || []).forEach((w: string) => toast.warning(w))
        setBought(res.result) // { purchased:[{e164,state_id,cost}], total_cost, count, warnings }
        await refetchInv()
      }
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.invToastAddFailed))
    }
  }

  const handleRelease = async (n: InvNumber) => {
    const warn =
      n.classification === 'messaging'
        ? intl.formatMessage(M.invConfirmWarnMessaging)
        : n.classification === 'pool'
        ? intl.formatMessage(M.invConfirmWarnPool)
        : ''
    const question = intl.formatMessage(M.invConfirmRelease, {
      number: n.phone_number,
      amount: `$${n.rental_estimate}`,
    })
    if (!window.confirm(warn ? `${question}\n\n${warn}` : question)) return
    setBusy(n.phone_number)
    try {
      const {data} = await releaseNumber({variables: {phoneNumber: n.phone_number}})
      if (data?.releaseTwilioNumber?.success) {
        toast.success(intl.formatMessage(M.invToastReleased, {number: n.phone_number}))
        ;(data.releaseTwilioNumber.warnings || []).forEach((w: string) => toast.warning(w))
        await refetchInv()
      }
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.invToastReleaseFailed))
    } finally {
      setBusy('')
    }
  }

  const exportCsv = () => {
    const rows = inventory?.numbers ?? []
    if (!rows.length) {
      toast.info(intl.formatMessage(M.invToastNothingToExport))
      return
    }
    const header = ['Phone Number', 'Friendly Name', 'Type', 'State', 'Monthly Rental (est.)', 'Purchased', 'Used For', 'Owner', 'Group', ...(isSuperAdmin ? ['Organization'] : []), 'Last Used', 'Stale']
    const body = rows.map((n) => [
      n.phone_number,
      `"${(n.friendly_name || '').replace(/"/g, '""')}"`,
      n.type,
      `"${stateName(n.state)}"`,
      n.rental_estimate,
      n.date_created,
      `"${classLabel(intl, n.classification)}"`,
      n.owner || '',
      `"${(n.group || '').replace(/"/g, '""')}"`,
      ...(isSuperAdmin ? [`"${(n.organization || '').replace(/"/g, '""')}"`] : []),
      n.last_used || '',
      intl.formatMessage(n.stale ? M.yes : M.no),
    ])
    const csv = [header.join(','), ...body.map((r) => r.join(','))].join('\n')
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'twilio-numbers.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  if (iError) {
    return (
      <Card className='mt-5'>
        <Card.Body className='text-center py-10'>
          <h4 className='mb-2'>{intl.formatMessage(M.invErrorTitle)}</h4>
          <p className='text-muted mb-0'>{iError.message}</p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <div className='mt-5'>
      <Card>
          <Card.Header className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
            <div>
              <Card.Title className='mb-1'>
                {inventory
                  ? intl.formatMessage(M.invTitleCount, {count: inventory.count})
                  : intl.formatMessage(M.invTitle)}
              </Card.Title>
              {inventory && (
                <div className='d-flex flex-wrap gap-2 align-items-center'>
                  {(['pool', 'messaging', 'policydial', 'unclassified'] as Classification[]).map((k) =>
                    inventory.counts?.[k] ? (
                      <span
                        key={k}
                        role='button'
                        title={
                          classFilter === k
                            ? intl.formatMessage(M.invClearFilter)
                            : intl.formatMessage(M.invFilterBy, {label: classLabel(intl, k)})
                        }
                        onClick={() => setClassFilter((prev) => (prev === k ? '' : k))}
                        className={`badge ${CLASS_BADGE[k]}`}
                        style={{
                          cursor: 'pointer',
                          outline: classFilter === k ? '2px solid currentColor' : 'none',
                          opacity: classFilter && classFilter !== k ? 0.45 : 1,
                        }}
                      >
                        {intl.formatMessage(T.labelCount, {
                          label: classLabel(intl, k),
                          count: inventory.counts[k],
                        })}
                      </span>
                    ) : null
                  )}
                  {inventory.stale_pool_count > 0 && (
                    <span className='badge badge-light-warning'>
                      {intl.formatMessage(M.invStalePool, {count: inventory.stale_pool_count})}
                    </span>
                  )}
                  {classFilter && (
                    <button
                      type='button'
                      className='btn btn-link p-0 fs-8 text-primary'
                      onClick={() => setClassFilter('')}
                    >
                      {intl.formatMessage(M.invClearFilter)}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className='d-flex align-items-center gap-3 flex-wrap'>
              <Form.Control
                size='sm'
                type='search'
                placeholder={intl.formatMessage(M.invSearchPlaceholder)}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{maxWidth: 180}}
              />
              <Form.Check
                type='switch'
                id='reviewOnly'
                label={intl.formatMessage(M.invReviewOnly)}
                checked={reviewOnly}
                onChange={(e) => setReviewOnly(e.target.checked)}
              />
              <Button variant='light-primary' size='sm' onClick={exportCsv}>
                {intl.formatMessage(M.invExportCsv)}
              </Button>
              <Button variant='primary' size='sm' onClick={openBuy}>
                {intl.formatMessage(M.invAddToPool)}
              </Button>
            </div>
          </Card.Header>
          <Card.Body className='p-0'>
            {iLoading && !inventory ? (
              <div className='p-6'><Spinner animation='border' size='sm' /></div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-row-dashed align-middle gy-2 mb-0'>
                  <thead>
                    <tr className='fw-bold text-muted' style={{cursor: 'pointer'}}>
                      <th className='ps-6' onClick={() => onSort('phone_number')}>
                        {intl.formatMessage(M.colNumber)}
                        {arrow('phone_number')}
                      </th>
                      <th onClick={() => onSort('type')}>
                        {intl.formatMessage(M.colType)}
                        {arrow('type')}
                      </th>
                      <th onClick={() => onSort('state')}>
                        {intl.formatMessage(M.colState)}
                        {arrow('state')}
                      </th>
                      <th onClick={() => onSort('date_created')}>
                        {intl.formatMessage(M.invColPurchased)}
                        {arrow('date_created')}
                      </th>
                      <th onClick={() => onSort('rental_estimate')}>
                        {intl.formatMessage(M.invColRental)}
                        {arrow('rental_estimate')}
                      </th>
                      <th onClick={() => onSort('classification')}>
                        {intl.formatMessage(M.invColUsedFor)}
                        {arrow('classification')}
                      </th>
                      <th onClick={() => onSort('owner')}>
                        {intl.formatMessage(M.invColOwnerLastUsed)}
                        {arrow('owner')}
                      </th>
                      <th onClick={() => onSort('group')}>
                        {intl.formatMessage(M.colGroup)}
                        {arrow('group')}
                      </th>
                      {isSuperAdmin && (
                        <th onClick={() => onSort('organization')}>
                          {intl.formatMessage(M.colOrganization)}
                          {arrow('organization')}
                        </th>
                      )}
                      <th className='text-end pe-6'>{intl.formatMessage(M.colActions)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((n) => (
                      <tr key={n.phone_number}>
                        <td className='ps-6 fw-bold'>{n.phone_number}</td>
                        <td>
                          <span className={`badge ${n.type === 'tollfree' ? 'badge-light-info' : 'badge-light-secondary'}`}>{n.type}</span>
                        </td>
                        <td>{stateName(n.state)}</td>
                        <td className='text-muted'>{dateOnly(n.date_created)}</td>
                        <td>{money(n.rental_estimate)}</td>
                        <td>
                          <span className={`badge ${CLASS_BADGE[n.classification]}`}>
                            {classLabel(intl, n.classification)}
                          </span>
                          {n.stale && (
                            <span className='badge badge-light-warning ms-1'>
                              {intl.formatMessage(M.invStale)}
                            </span>
                          )}
                        </td>
                        <td className='text-muted'>
                          {n.owner ||
                            (n.last_used ? (
                              intl.formatMessage(M.invUsedOn, {date: n.last_used.slice(0, 10)})
                            ) : (
                              <span className='text-muted'>{EM_DASH}</span>
                            ))}
                        </td>
                        <td className='text-muted'>{n.group || EM_DASH}</td>
                        {isSuperAdmin && (
                          <td className='text-muted'>{n.organization || EM_DASH}</td>
                        )}
                        <td className='text-end pe-6'>
                          <Button
                            variant='light-danger'
                            size='sm'
                            disabled={busy === n.phone_number}
                            onClick={() => handleRelease(n)}
                          >
                            {busy === n.phone_number ? (
                              <Spinner animation='border' size='sm' />
                            ) : (
                              intl.formatMessage(T.release)
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(pageCount > 1 || filtered.length === 0) && (
                  <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 px-6 py-3'>
                    <span className='text-muted fs-8'>
                      {filtered.length === 0
                        ? intl.formatMessage(M.invNoMatches)
                        : intl.formatMessage(M.showingRange, {
                            start: String((page - 1) * PAGE_SIZE + 1),
                            end: String(Math.min(page * PAGE_SIZE, filtered.length)),
                            total: String(filtered.length),
                          })}
                    </span>
                    {pageCount > 1 && (
                      <div className='d-flex align-items-center gap-2'>
                        <Button variant='light' size='sm' disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                          {intl.formatMessage(M.previous)}
                        </Button>
                        <span className='text-muted fs-8'>
                          {intl.formatMessage(M.pageIndicator, {
                            page: String(page),
                            total: String(pageCount),
                          })}
                        </span>
                        <Button variant='light' size='sm' disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                          {intl.formatMessage(M.next)}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        <Modal show={showBuy} onHide={closeBuy} centered>
          <Modal.Header closeButton>
            <Modal.Title>{intl.formatMessage(bought ? M.buyTitleDone : M.buyTitle)}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!bought ? (
              <>
                <div className='text-muted fs-7 mb-4'>
                  {intl.formatMessage(M.buyDescription)}
                </div>
                <Row className='g-3'>
                  <Col sm={12}>
                    <Form.Label className='small text-muted mb-1'>
                      {intl.formatMessage(M.colState)}
                    </Form.Label>
                    <Form.Select value={poolState} onChange={(e) => setPoolState(e.target.value)}>
                      <option value=''>{intl.formatMessage(T.selectState)}</option>
                      {US_STATES.map((s: any) => (
                        <option key={s.code || s.value} value={s.code || s.value}>
                          {stateOption(s)}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col sm={6}>
                    <Form.Label className='small text-muted mb-1'>
                      {intl.formatMessage(M.buyAreaCode)}
                    </Form.Label>
                    <Form.Control
                      placeholder={intl.formatMessage(M.buyAreaCodePlaceholder)}
                      value={poolArea}
                      onChange={(e) => setPoolArea(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                    />
                  </Col>
                  <Col sm={6}>
                    <Form.Label className='small text-muted mb-1'>
                      {intl.formatMessage(M.buyQuantity)}
                    </Form.Label>
                    <Form.Control
                      type='number'
                      min={1}
                      max={10}
                      value={poolQty}
                      onChange={(e) => setPoolQty(e.target.value)}
                    />
                  </Col>
                </Row>
                <div className='text-muted fs-8 mt-3'>
                  {intl.formatMessage(M.buyEstCost, {
                    amount: `$${(
                      Math.max(1, Math.min(10, parseInt(poolQty || '1', 10) || 1)) * 1.15
                    ).toFixed(2)}`,
                  })}
                </div>
              </>
            ) : (
              <>
                <div className='alert alert-success py-3'>
                  <FormattedMessage
                    {...M.buyResult}
                    values={{
                      count: bought.count,
                      state: stateName(bought.state_id),
                      b: (chunks) => <b>{chunks}</b>,
                    }}
                  />
                </div>
                <div className='table-responsive'>
                  <table className='table table-row-dashed align-middle mb-0'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th>{intl.formatMessage(M.colNumber)}</th>
                        <th>{intl.formatMessage(M.colState)}</th>
                        <th className='text-end'>{intl.formatMessage(M.buyColCost)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(bought.purchased || []).map((p: any) => (
                        <tr key={p.e164}>
                          <td className='fw-bold'>{p.e164}</td>
                          <td>{stateName(p.state_id)}</td>
                          <td className='text-end'>{money(p.cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className='fw-bolder border-top'>
                        <td>{intl.formatMessage(M.total)}</td>
                        <td></td>
                        <td className='text-end'>{money(bought.total_cost)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {(bought.warnings || []).length > 0 && (
                  <div className='alert alert-warning py-2 mt-3 fs-8 mb-0'>
                    {(bought.warnings || []).map((w: string, i: number) => <div key={i}>{w}</div>)}
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {!bought ? (
              <>
                <Button variant='light' onClick={closeBuy} disabled={buyingPool}>
                  {intl.formatMessage(M.cancel)}
                </Button>
                <Button variant='primary' onClick={handleAddToPool} disabled={buyingPool || !poolState}>
                  {buyingPool ? (
                    <Spinner animation='border' size='sm' />
                  ) : (
                    intl.formatMessage(M.buyConfirm)
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant='light' onClick={() => setBought(null)}>
                  {intl.formatMessage(M.buyMore)}
                </Button>
                <Button variant='primary' onClick={closeBuy}>
                  {intl.formatMessage(M.done)}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
    </div>
  )
}
